/**
 * SoloStacksCounter — top-right session net ticker for Solo Grind. Shows how
 * many Stacks you're up or down this session with a stock-ticker feel: green
 * with an up arrow when ahead, red with a down arrow when behind. Solo is a
 * non-persistent sandbox, so this is the whole scoreboard.
 *
 * Baseline = the first balance seen after show(); net = current - baseline.
 */

class SoloStacksCounterImpl {
  private root: HTMLElement | null = null;
  private arrowEl: HTMLElement | null = null;
  private amountEl: HTMLElement | null = null;
  private baseline: number | null = null;
  private suppressed = false;
  private onTap: (() => void) | null = null;

  private ensure(): void {
    if (this.root) return;
    this.injectStyles();
    const root = document.createElement('div');
    root.className = 'solo-stacks-counter';
    root.innerHTML = `
      <span class="solo-stacks-history">≡</span>
      <span class="solo-stacks-arrow"></span>
      <span class="solo-stacks-amount">0</span>`;
    root.addEventListener('click', () => this.onTap?.());
    document.body.appendChild(root);
    this.root = root;
    this.arrowEl = root.querySelector('.solo-stacks-arrow');
    this.amountEl = root.querySelector('.solo-stacks-amount');
  }

  /** Show the ticker. `onTap` (when given) makes it tappable — opens the roll
   *  history sheet — and reveals the small ledger glyph as the affordance. */
  show(onTap?: () => void): void {
    this.ensure();
    this.onTap = onTap ?? null;
    this.baseline = null;
    this.setNet(0);
    const root = this.root!;
    root.classList.toggle('tappable', !!onTap);
    root.classList.toggle('suppressed', this.suppressed);
    void root.offsetHeight; // commit hidden base before fade-in
    root.classList.add('visible');
  }

  hide(): void {
    this.onTap = null;
    this.root?.classList.remove('visible');
    this.baseline = null;
  }

  /**
   * Temporarily fade the counter out without tearing it down (used while the
   * solo game-select modal is up — its close button sits in the same corner).
   * Unlike hide(), the session baseline survives, so the net keeps tracking.
   */
  setSuppressed(on: boolean): void {
    this.suppressed = on;
    this.root?.classList.toggle('suppressed', on);
  }

  /** Feed the local player's current balance; the counter tracks the delta. */
  setBalance(balance: number): void {
    if (!isFinite(balance)) return;
    if (this.baseline === null) this.baseline = balance;
    this.setNet(balance - this.baseline);
  }

  private setNet(net: number): void {
    if (!this.root || !this.arrowEl || !this.amountEl) return;
    const up = net >= 0;
    this.root.classList.toggle('up', up);
    this.root.classList.toggle('down', !up);
    this.arrowEl.textContent = up ? '▲' : '▼';
    this.amountEl.textContent = `${up ? '+' : '-'}${formatCompact(Math.abs(net))}`;
  }

  private injectStyles(): void {
    if (document.getElementById('solo-stacks-styles')) return;
    const style = document.createElement('style');
    style.id = 'solo-stacks-styles';
    style.textContent = `
      .solo-stacks-counter {
        position: fixed; top: calc(env(safe-area-inset-top, 0px) + 14px); right: 14px;
        z-index: 60; display: flex; align-items: center; gap: 6px;
        padding: 7px 13px; border-radius: 22px;
        background: linear-gradient(160deg, rgba(28,28,34,0.94), rgba(16,16,20,0.94));
        border: 2px solid rgba(255,255,255,0.14);
        font-family: 'Bebas Neue', Impact, sans-serif; font-size: 20px; letter-spacing: 0.5px;
        opacity: 0; transform: translateY(-6px); pointer-events: none;
        transition: opacity 220ms ease, transform 220ms ease;
      }
      .solo-stacks-counter.visible { opacity: 1; transform: translateY(0); }
      .solo-stacks-counter.visible.tappable { pointer-events: auto; cursor: pointer; }
      /* Suppressed = invisible; must never stay a tappable ghost. */
      .solo-stacks-counter.visible.suppressed { opacity: 0; transform: translateY(-6px); pointer-events: none; }
      .solo-stacks-counter.visible.tappable:active { transform: translateY(0) scale(0.96); }
      .solo-stacks-history {
        display: none;
        font-size: 15px; line-height: 1; color: rgba(255,255,255,0.55);
      }
      .solo-stacks-counter.tappable .solo-stacks-history { display: inline; }
      .solo-stacks-arrow { font-size: 13px; line-height: 1; }
      .solo-stacks-counter.up { border-color: rgba(0,255,136,0.5); color: #38F5A0; }
      .solo-stacks-counter.up .solo-stacks-arrow { color: #38F5A0; }
      .solo-stacks-counter.down { border-color: rgba(255,51,102,0.5); color: #FF5A7A; }
      .solo-stacks-counter.down .solo-stacks-arrow { color: #FF5A7A; }
    `;
    document.head.appendChild(style);
  }
}

/** Compact Stacks formatter (1.2B / 340M / 50K) — shared with the roll-history sheet. */
export function formatCompact(n: number): string {
  if (!isFinite(n) || n <= 0) return '0';
  const units: Array<{ v: number; s: string }> = [
    { v: 1e12, s: 'T' }, { v: 1e9, s: 'B' }, { v: 1e6, s: 'M' }, { v: 1e3, s: 'K' },
  ];
  for (const u of units) {
    if (n >= u.v) {
      const scaled = n / u.v;
      let str = scaled >= 100 ? Math.floor(scaled).toString()
        : scaled >= 10 ? (Math.floor(scaled * 10) / 10).toFixed(1)
        : (Math.floor(scaled * 100) / 100).toFixed(2);
      if (str.includes('.')) str = str.replace(/0+$/, '').replace(/\.$/, '');
      return `${str}${u.s}`;
    }
  }
  return Math.floor(n).toString();
}

/** Always-mounted singleton (created on first show). */
export const soloStacksCounter = new SoloStacksCounterImpl();
